import { useEffect, useState } from "react";
import { Badge, Button, Card, EmptyState, Heading, Spinner, Stack, Text } from "@/components/ds";
import { usePageTitle } from "../hooks/usePageTitle";
import type { TemplateName } from "@/services/email";

interface CapturedEmail {
  readonly to: string;
  readonly subject: string;
  readonly html: string;
  readonly text: string;
  readonly template: TemplateName;
  readonly status: string;
  readonly sentAt: string;
}

export function DevEmailsPage() {
  usePageTitle("Dev · Sent emails");
  const [emails, setEmails] = useState<readonly CapturedEmail[] | null>(null);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [view, setView] = useState<"html" | "text">("html");

  function load() {
    setEmails(null);
    fetch("/api/dev/emails")
      .then((res) => res.ok ? res.json() : { success: false })
      .then((json: { success: boolean; data?: { emails: CapturedEmail[] } }) => {
        setEmails(json.success && json.data ? json.data.emails : []);
      });
  }

  useEffect(() => {
    load();
  }, []);

  async function handleClear() {
    await fetch("/api/dev/emails/clear", { method: "POST" });
    load();
  }

  if (emails === null) {
    return (
      <div style={{ padding: "var(--space-6)", display: "flex", justifyContent: "center" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ padding: "var(--space-6)", maxWidth: 960, margin: "0 auto" }}>
      <Stack gap={4}>
        <div>
          <Heading level={1} size="h3">Captured emails</Heading>
          <Text color="muted">Dev only. Shows the last 50 emails the mock provider captured.</Text>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          {emails.length > 0 && (
            <Button variant="secondary" size="sm" onClick={handleClear}>Clear captured</Button>
          )}
        </div>

        {emails.length === 0 ? (
          <Card><EmptyState title="No emails yet" description="Trigger an invite or password reset to see captured email here." /></Card>
        ) : (
          <Stack gap={2}>
            {emails.map((email, idx) => {
              const isOpen = expandedIdx === idx;
              return (
                <Card key={`${email.sentAt}-${idx}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedIdx(isOpen ? null : idx)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpandedIdx(isOpen ? null : idx); }}
                    style={{ cursor: "pointer", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}
                  >
                    <Badge variant="info">{email.template}</Badge>
                    <strong>{email.to}</strong>
                    <span style={{ color: "var(--color-text-muted)", flex: 1 }}>{email.subject}</span>
                    <span style={{ color: "var(--color-text-muted)", fontSize: 13 }}>{new Date(email.sentAt).toLocaleString()}</span>
                  </div>
                  {isOpen && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                        <Button variant={view === "html" ? "primary" : "secondary"} size="sm" onClick={() => setView("html")}>HTML</Button>
                        <Button variant={view === "text" ? "primary" : "secondary"} size="sm" onClick={() => setView("text")}>Text</Button>
                      </div>
                      {view === "html" ? (
                        <iframe
                          title={`${email.template}-preview-${idx}`}
                          srcDoc={email.html}
                          style={{ width: "100%", minHeight: 480, border: "1px solid var(--color-border-subtle)", borderRadius: 8, background: "white" }}
                        />
                      ) : (
                        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 13, background: "var(--color-bg-subtle)", padding: 12, borderRadius: 8, margin: 0 }}>
                          {email.text}
                        </pre>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>
    </div>
  );
}
